import React, { useState, useEffect } from 'react';
import { PageHeader, FormSection, Badge, Modal } from '../components/ui/SharedUI';
import { useFeedback } from '../components/ui/FeedbackUI';
import { demoRepository } from '../repository/demoRepository';
import { calculations } from '../utils/calculations';
import { pocketbaseService, REQUIRED_COLLECTIONS } from '../services/pocketbaseService';
import SafeIcon from '../common/SafeIcon';

const SAFE_DEFAULT_STATUS = {
  status: 'demo_mode',
  isConnected: false,
  isDemoMode: true,
  isInitializing: false,
  isConfigured: false,
  error: null,
  url: 'Not configured',
  activeDataSource: 'demo',
  collectionStatus: {},
  lastHealthCheck: null,
  envVarAvailable: false
};

const COLLECTION_GROUPS = [
  {
    title: 'Core Collections',
    description: 'Required for basic app functionality',
    collections: [
      'properties', 'rentable_spaces', 'tenants', 'leases', 'rent_schedule',
      'transactions', 'documents', 'inspections', 'insurance_policies',
      'open_items', 'settings', 'audit_logs'
    ]
  },
  {
    title: 'Finance Readiness',
    description: 'Loan splits, equity release and valuation tracking',
    collections: ['loans', 'loan_events', 'valuations']
  },
  {
    title: 'Agent Readiness',
    description: 'Property manager and agent assignment tracking',
    collections: ['agents', 'property_agent_assignments']
  },
  {
    title: 'Communication Readiness',
    description: 'Email, SMS, notification and activity infrastructure',
    collections: [
      'communication_logs', 'email_jobs', 'sms_jobs',
      'message_templates', 'notification_events',
      'activity_logs', 'system_settings'
    ]
  }
];

const SettingsPage = () => {
  const { showConfirm, showToast, showSyncProgress } = useFeedback();
  const [backendStatus, setBackendStatus] = useState(SAFE_DEFAULT_STATUS);
  const [collectionStatus, setCollectionStatus] = useState({});
  const [isTesting, setIsTesting] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isSeeding, setIsSeeding] = useState(false);
  const [lastTestTime, setLastTestTime] = useState(null);
  const [activeDataSource, setActiveDataSource] = useState('demo');
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState(null);

  const settings = demoRepository.getAppSettings();
  const summary = calculations.getPortfolioSummary();
  const emailConfig = pocketbaseService.email.getConfig();

  useEffect(() => {
    refreshBackendStatus();
  }, []);

  const refreshBackendStatus = () => {
    try {
      const status = pocketbaseService.getStatus();
      setBackendStatus(status || SAFE_DEFAULT_STATUS);
      setCollectionStatus(status?.collectionStatus || {});
      setActiveDataSource(status?.activeDataSource || 'demo');
      setLastTestTime(status?.lastHealthCheck ? new Date(status.lastHealthCheck) : null);
    } catch (error) {
      setBackendStatus(SAFE_DEFAULT_STATUS);
    }
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    try {
      const health = await pocketbaseService.getHealth();
      const status = pocketbaseService.getStatus();
      setBackendStatus(status || SAFE_DEFAULT_STATUS);
      setLastTestTime(new Date());
      if (health.success) {
        showToast(`Backend reachable - ${health.url}`, 'success');
      } else {
        showToast(`Backend status: ${health.status}`, 'info');
      }
    } catch (error) {
      showToast('Connection test failed', 'error');
      setBackendStatus(SAFE_DEFAULT_STATUS);
    } finally {
      setIsTesting(false);
    }
  };

  const handleCheckCollections = async () => {
    setIsChecking(true);
    try {
      if (!backendStatus?.isConnected) {
        const health = await pocketbaseService.getHealth();
        if (!health.success) {
          showToast('Backend not connected. Run "Test Backend Connection" first.', 'info');
          setIsChecking(false);
          return;
        }
      }
      const result = await pocketbaseService.checkCollections(REQUIRED_COLLECTIONS);
      const status = pocketbaseService.getStatus();
      setCollectionStatus(result.collections || {});
      setBackendStatus(status || SAFE_DEFAULT_STATUS);
      setActiveDataSource(status?.activeDataSource || 'demo');
      setLastTestTime(new Date());
      if (result.summary.ready > 0) {
        showToast(`${result.summary.ready} collections ready`, 'success');
      } else if (result.summary.empty > 0) {
        showToast(`Backend connected but ${result.summary.empty} collections empty`, 'info');
      } else {
        showToast('Collections check complete', 'info');
      }
    } catch (error) {
      showToast('Collection check failed', 'error');
    } finally {
      setIsChecking(false);
    }
  };

  const handlePullData = async () => {
    try {
      if (!pocketbaseService.isEnabled) {
        showToast('PocketBase is not configured. Set VITE_POCKETBASE_URL to enable backend sync.', 'error');
        return;
      }
      const result = await showSyncProgress({
        title: 'Syncing PocketBase',
        stages: [
          'Checking backend connection',
          'Loading PocketBase records',
          'Updating frontend cache',
          'Refreshing visible pages'
        ],
        action: () => demoRepository.refreshFromPocketBase({ force: true, silent: true, notify: true }),
        successMessage: 'PocketBase sync complete.',
        errorMessage: 'PocketBase sync failed.'
      });
      if (result?.success) {
        setActiveDataSource(result.mode);
        const freshStatus = pocketbaseService.getStatus();
        setBackendStatus(freshStatus || SAFE_DEFAULT_STATUS);
        setCollectionStatus(freshStatus?.collectionStatus || result.recordCounts || {});
        showToast(`Loaded ${result.totalRecords || 0} records from PocketBase`, 'success');
      } else if (result?.success === false) {
        if (result?.mode === 'demo') {
          showToast('Sync skipped — app is in demo mode. Use "Use Demo Data" toggle to reconnect.', 'info');
        } else {
          showToast(result?.error || 'PocketBase sync failed', 'error');
        }
      }
    } catch (error) {
      showToast(error?.message || 'PocketBase sync failed', 'error');
    }
  };

  const handleUseDemoData = () => {
    showConfirmModal({
      title: 'Switch to Demo Data?',
      message: 'This will reset all data to local demo arrays. Any data loaded from PocketBase will be replaced with demo data in the UI. The app will stay in demo mode until you click Force Sync or Pull Data.',
      confirmText: 'Use Demo Data',
      isDestructive: false,
      onConfirm: () => {
        demoRepository.resetToDemoData();
        pocketbaseService.setActiveDataSource('demo');
        setActiveDataSource('demo');
        showToast('Switched to demo data', 'success');
        refreshBackendStatus();
      }
    });
  };

  const handleSeedDemoData = async () => {
    setIsSeeding(true);
    try {
      if (!backendStatus?.isConnected) {
        const health = await pocketbaseService.getHealth();
        if (!health.success) {
          showToast('Backend not connected. Run "Test Backend Connection" first.', 'info');
          setIsSeeding(false);
          return;
        }
      }
      showConfirmModal({
        title: 'Seed Demo Data to PocketBase?',
        message: 'This will push all demo records to your PocketBase backend. Existing records with matching IDs may be overwritten. This action cannot be undone.',
        confirmText: 'Seed Demo Data',
        isDestructive: true,
        onConfirm: async () => {
          try {
            const demoData = {
              properties: demoRepository.getAllProperties(),
              spaces: demoRepository.getPortfolio().totalSpaces > 0
                ? demoRepository.getAllProperties().flatMap(p => demoRepository.getSpacesByProperty(p.id))
                : [],
              tenants: demoRepository.getAllTenants(),
              leases: demoRepository.getAllLeases(),
              transactions: demoRepository.getTransactions(),
              insurance: demoRepository.getAllInsurancePolicies(),
              documents: demoRepository.getDocuments(),
              inspections: demoRepository.getInspections(),
              loans: demoRepository.getAllLoans(),
              openItems: demoRepository.getAllOpenItems()
            };
            const result = await pocketbaseService.seedDemoData(demoData);
            if (result.success) {
              showToast(`Seeded ${result.seeded} records to PocketBase`, 'success');
              await handlePullData();
            } else {
              showToast(`Seeding failed: ${result.message}`, 'error');
            }
          } catch (error) {
            showToast('Seeding failed', 'error');
          } finally {
            setIsSeeding(false);
          }
        }
      });
    } catch (error) {
      setIsSeeding(false);
      showToast('Seeding failed', 'error');
    }
  };

  const showConfirmModal = (config) => {
    setConfirmConfig(config);
    setIsConfirmModalOpen(true);
  };

  const getDataSourceBadge = () => {
    const config = {
      demo: { label: 'Demo Data', status: 'pending', color: 'bg-slate-100 text-slate-700' },
      backend_ready: { label: 'PocketBase Data', status: 'active', color: 'bg-emerald-100 text-emerald-700' },
      backend_empty: { label: 'Backend Empty', status: 'pending', color: 'bg-amber-100 text-amber-700' },
      backend_unavailable: { label: 'Backend Blocked', status: 'overdue', color: 'bg-red-100 text-red-700' },
      backend_loading: { label: 'Backend Loading', status: 'pending', color: 'bg-slate-100 text-slate-500' }
    };
    return config[activeDataSource] || config.demo;
  };

  const getConnectionBadge = () => {
    if (!backendStatus) return { label: 'Demo Mode', status: 'pending' };
    if (backendStatus.isConnected) return { label: 'Connected', status: 'active' };
    if (backendStatus.isDemoMode) return { label: 'Demo Mode', status: 'pending' };
    if (backendStatus.status === 'unreachable') return { label: 'Unreachable', status: 'overdue' };
    if (backendStatus.status === 'initializing') return { label: 'Connecting...', status: 'pending' };
    return { label: 'Disconnected', status: 'default' };
  };

  const getCollectionBadge = (status) => {
    const config = {
      ready: { label: 'Ready', status: 'active' },
      empty: { label: 'Empty', status: 'pending' },
      missing: { label: 'Missing', status: 'overdue' },
      blocked: { label: 'Blocked', status: 'overdue' },
      error: { label: 'Error', status: 'overdue' }
    };
    const c = config[status] || config.error;
    return <Badge label={c.label} status={c.status} />;
  };

  const dataSourceBadge = getDataSourceBadge();
  const connectionBadge = getConnectionBadge();

  return (
    <div className="animate-fade-in max-w-6xl">
      <PageHeader title="Settings & System Configuration" subtitle="Manage application preferences and backend integration." />

      {/* Active Data Source Banner */}
      <FormSection className="mb-6">
        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${
              activeDataSource === 'backend_ready' ? 'bg-emerald-500' :
              activeDataSource === 'backend_empty' ? 'bg-amber-500' :
              activeDataSource === 'backend_unavailable' ? 'bg-red-500' : 'bg-slate-400'
            }`} />
            <div>
              <div className="text-sm text-slate-600">Active Data Source</div>
              <div className="font-medium text-slate-800">{dataSourceBadge.label}</div>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${dataSourceBadge.color}`}>
            {dataSourceBadge.label}
          </span>
        </div>
      </FormSection>

      {/* Backend Connection Status */}
      <FormSection title="Backend Connection" description="PocketBase/PocketHost integration status and configuration" className="mb-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${
                backendStatus?.isConnected && activeDataSource === 'backend_ready' ? 'bg-emerald-500' :
                backendStatus?.isConnected ? 'bg-amber-500' :
                backendStatus?.isDemoMode ? 'bg-slate-400' :
                backendStatus?.status === 'unreachable' ? 'bg-red-500' : 'bg-slate-400'
              }`} />
              <div>
                <div className="font-medium text-slate-800">
                  {backendStatus?.isConnected
                    ? activeDataSource === 'backend_ready' ? 'PocketBase Active'
                    : activeDataSource === 'backend_empty' ? 'Backend Connected, Collections Empty'
                    : 'Backend Connected, Using Demo Fallback'
                    : backendStatus?.isDemoMode ? 'Demo Mode (Local Data)'
                    : backendStatus?.status === 'unreachable' ? 'Backend Unreachable'
                    : 'Disconnected'}
                </div>
                <div className="text-sm text-slate-500">
                  {backendStatus?.url || 'No PocketBase URL configured'}
                </div>
              </div>
            </div>
            <Badge label={connectionBadge.label} status={connectionBadge.status} />
          </div>

          {backendStatus?.error && (
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-start gap-3">
                <SafeIcon icon="Info" className="text-slate-600 text-lg flex-shrink-0" />
                <div>
                  <div className="text-sm font-medium text-slate-800">Connection Status</div>
                  <div className="text-sm text-slate-700 mt-1">{backendStatus.error}</div>
                  <div className="text-xs text-slate-600 mt-2">App will continue in demo mode with local data.</div>
                </div>
              </div>
            </div>
          )}

          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <h4 className="text-sm font-semibold text-slate-800 mb-3">Configuration</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">PocketBase URL:</span>
                <span className="font-mono text-slate-700">{backendStatus?.url || 'Not set'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Environment Variable:</span>
                <span className={`font-mono ${backendStatus?.envVarAvailable ? 'text-emerald-600' : 'text-red-600'}`}>
                  VITE_POCKETBASE_URL {backendStatus?.envVarAvailable ? '✓' : '✗'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Active Data Source:</span>
                <span className="font-medium capitalize">{activeDataSource.replace('_', ' ')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Last Health Check:</span>
                <span className="font-medium">{lastTestTime ? calculations.formatDateAU(lastTestTime) : 'Never'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Mode:</span>
                <span className="font-medium">
                  {activeDataSource === 'backend_ready' ? 'Production (PocketBase)'
                    : activeDataSource === 'backend_empty' ? 'Backend Empty'
                    : activeDataSource === 'backend_unavailable' ? 'Backend Unavailable'
                    : 'Demo (Local)'}
                </span>
              </div>
            </div>
          </div>

          {/* Backend Action Buttons */}
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <h4 className="text-sm font-semibold text-slate-800 mb-3">Backend Diagnostics & Actions</h4>
            <div className="flex flex-wrap gap-3">
              <button onClick={handleTestConnection} disabled={isTesting}
                className="px-4 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg disabled:opacity-50">
                {isTesting ? 'Testing...' : 'Test Backend Connection'}
              </button>
              <button onClick={handleCheckCollections} disabled={isChecking || !backendStatus?.isConnected}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50">
                {isChecking ? 'Checking...' : 'Check Collections'}
              </button>
              <button onClick={handlePullData} disabled={!pocketbaseService.isEnabled}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg disabled:opacity-50">
                Pull Data From PocketBase
              </button>
              <button onClick={handleSeedDemoData} disabled={isSeeding || !backendStatus?.isConnected}
                className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg disabled:opacity-50">
                {isSeeding ? 'Seeding...' : 'Seed Demo Data To PocketBase'}
              </button>
              <button onClick={handleUseDemoData}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 rounded-lg">
                Use Demo Data
              </button>
              <button onClick={refreshBackendStatus}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 rounded-lg">
                Refresh Status
              </button>
            </div>
          </div>
        </div>
      </FormSection>

      {/* Collection Checklist — Grouped by Readiness */}
      <FormSection title="Required Collections Checklist" description="PocketBase collections grouped by readiness category" className="mb-6">
        {Object.keys(collectionStatus).length > 0 ? (
          <div className="space-y-6">
            {COLLECTION_GROUPS.map(group => (
              <div key={group.title}>
                <div className="flex items-center gap-2 mb-3">
                  <h4 className="text-sm font-semibold text-slate-800">{group.title}</h4>
                  <span className="text-xs text-slate-500">{group.description}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {group.collections.map(collection => {
                    const status = collectionStatus[collection];
                    const readyCount = status?.status === 'ready' ? 1 : 0;
                    return (
                      <div key={collection} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <div>
                          <span className="text-xs font-medium text-slate-700">{collection}</span>
                          {status && (
                            <div className="text-xs text-slate-500 mt-0.5">
                              {status.recordCount !== undefined ? `${status.recordCount} records` : ''}
                            </div>
                          )}
                        </div>
                        {status ? getCollectionBadge(status.status) : <Badge label="Unknown" status="default" />}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 text-center text-sm text-slate-600">
            Click "Check Collections" to verify collection status
          </div>
        )}
      </FormSection>

      {/* Backend Mode Information */}
      <FormSection title="Backend Mode Information" description="Understanding data source modes" className="mb-6">
        <div className="space-y-3 text-sm text-slate-700">
          <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
            <strong className="font-medium text-emerald-800">PocketBase Active:</strong>
            <span className="text-emerald-700 ml-2">Backend connected, collections ready, data loaded from PocketBase.</span>
          </div>
          <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
            <strong className="font-medium text-amber-800">Backend Empty:</strong>
            <span className="text-amber-700 ml-2">Backend connected but collections have no records. Use "Seed Demo Data" to populate.</span>
          </div>
          <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
            <strong className="font-medium text-amber-800">Backend Unavailable:</strong>
            <span className="text-amber-700 ml-2">Backend connected but collections missing or blocked by rules. Using demo fallback.</span>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <strong className="font-medium text-slate-800">Demo Mode:</strong>
            <span className="text-slate-700 ml-2">Using local demo data. No backend connection required. Click "Force Sync" in the topbar or "Pull Data" here to reconnect.</span>
          </div>
          <p className="text-xs mt-3 text-slate-600">
            Note: Login/authentication is not implemented. All operations use public PocketBase access or demo data.
          </p>
        </div>
      </FormSection>

      {/* Email Relay Configuration */}
      <FormSection title="Email Relay Configuration" description="Google Apps Script email integration status" className="mb-6">
        <div className="space-y-3">
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Relay URL Configured:</span>
                <span className={`font-medium ${emailConfig.isConfigured ? 'text-emerald-600' : 'text-slate-400'}`}>
                  {emailConfig.isConfigured ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Relay Key Configured:</span>
                <span className={`font-medium ${emailConfig.relayKey === 'configured' ? 'text-emerald-600' : 'text-slate-400'}`}>
                  {emailConfig.relayKey === 'configured' ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Environment Variable:</span>
                <span className="font-mono text-slate-700">VITE_GAS_RELAY_URL</span>
              </div>
              {emailConfig.defaultCC && (
                <div className="flex justify-between">
                  <span className="text-slate-600">Default CC:</span>
                  <span className="font-mono text-slate-700">{emailConfig.defaultCC}</span>
                </div>
              )}
              {emailConfig.defaultBCC && (
                <div className="flex justify-between">
                  <span className="text-slate-600">Default BCC:</span>
                  <span className="font-mono text-slate-700">{emailConfig.defaultBCC}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t">
                <span className="text-slate-600">Status:</span>
                <Badge label={emailConfig.isConfigured ? 'Configured' : 'Not Configured'} status={emailConfig.isConfigured ? 'active' : 'default'} />
              </div>
            </div>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-sm text-slate-600">
            <SafeIcon icon="Info" className="inline text-slate-600 mr-2" />
            Emails are sent via Google Apps Script relay. Authentication handled server-side. No credentials exposed in frontend.
          </div>
        </div>
      </FormSection>

      {/* Portfolio Summary */}
      <FormSection title={`Portfolio Summary (${dataSourceBadge.label})`} description="Live calculations from repository" className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="p-4 border border-slate-200 rounded-lg bg-slate-50">
            <div className="text-xs text-slate-500 mb-1">Total Market Value</div>
            <div className="text-lg font-semibold text-slate-800">{calculations.formatCurrencyAUD(summary.totalMarketValue)}</div>
          </div>
          <div className="p-4 border border-slate-200 rounded-lg bg-slate-50">
            <div className="text-xs text-slate-500 mb-1">Total Equity</div>
            <div className="text-lg font-semibold text-slate-800">{calculations.formatCurrencyAUD(summary.totalEquity)}</div>
          </div>
          <div className="p-4 border border-slate-200 rounded-lg bg-slate-50">
            <div className="text-xs text-slate-500 mb-1">Portfolio LVR</div>
            <div className="text-lg font-semibold text-slate-800">{(summary.portfolioLVR * 100).toFixed(2)}%</div>
          </div>
          <div className="p-4 border border-slate-200 rounded-lg bg-slate-50">
            <div className="text-xs text-slate-500 mb-1">Annual Rent</div>
            <div className="text-lg font-semibold text-slate-800">{calculations.formatCurrencyAUD(summary.annualRent)}</div>
          </div>
          <div className="p-4 border border-slate-200 rounded-lg bg-slate-50">
            <div className="text-xs text-slate-500 mb-1">Net Yield</div>
            <div className="text-lg font-semibold text-slate-800">{(summary.netYield * 100).toFixed(2)}%</div>
          </div>
          <div className="p-4 border border-slate-200 rounded-lg bg-slate-50">
            <div className="text-xs text-slate-500 mb-1">Occupancy Rate</div>
            <div className="text-lg font-semibold text-slate-800">{(summary.occupancyRate * 100).toFixed(2)}%</div>
          </div>
        </div>
      </FormSection>

      {/* Global Settings */}
      <FormSection title="Global Settings (Placeholder)" description="Future configuration options for the application" className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 border border-slate-200 rounded-lg bg-slate-50 text-sm text-slate-600">
            <div className="font-medium mb-1">Financial Year Start</div>
            <div>Month {settings.financialYearStartMonth} (July)</div>
          </div>
          <div className="p-4 border border-slate-200 rounded-lg bg-slate-50 text-sm text-slate-600">
            <div className="font-medium mb-1">Default Currency</div>
            <div>{settings.defaultCurrency}</div>
          </div>
          <div className="p-4 border border-slate-200 rounded-lg bg-slate-50 text-sm text-slate-600">
            <div className="font-medium mb-1">Backend Provider</div>
            <div>{activeDataSource === 'backend_ready' ? 'PocketBase' : 'Local (Demo)'}</div>
          </div>
          <div className="p-4 border border-slate-200 rounded-lg bg-slate-50 text-sm text-slate-600">
            <div className="font-medium mb-1">Email Enabled</div>
            <div>{settings.emailEnabled ? 'Yes' : 'No'}</div>
          </div>
          <div className="p-4 border border-slate-200 rounded-lg bg-slate-50 text-sm text-slate-600">
            <div className="font-medium mb-1">Property Defaults</div>
            <div>Notice: {settings.defaultNoticePeriodDays} days</div>
          </div>
          <div className="p-4 border border-slate-200 rounded-lg bg-slate-50 text-sm text-slate-600">
            <div className="font-medium mb-1">Authentication</div>
            <div>{settings.authEnabled ? 'Enabled' : 'Disabled'}</div>
          </div>
        </div>
      </FormSection>

      {/* Confirmation Modal */}
      {isConfirmModalOpen && confirmConfig && (
        <Modal isOpen={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)} title={confirmConfig.title}>
          <div className="space-y-6">
            <p className="text-slate-600">{confirmConfig.message}</p>
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <button onClick={() => setIsConfirmModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">
                Cancel
              </button>
              <button onClick={() => {
                if (confirmConfig.onConfirm) confirmConfig.onConfirm();
                setIsConfirmModalOpen(false);
              }}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg ${
                  confirmConfig.isDestructive ? 'bg-red-600 hover:bg-red-700' : 'bg-slate-900 hover:bg-slate-800'
                }`}>
                {confirmConfig.confirmText}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default SettingsPage;